import DashboardLayout from '@/components/layout/DashboardLayout';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useBookingDetails } from '@/hooks/use-booking-details';
import { usePayments } from '@/hooks/use-payments';
import { supabase } from '@/integrations/supabase/client';
import { api } from '@/api/api-client';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import {
  BadgePoundSterling,
  Calendar,
  CheckCircle,
  ChevronLeft,
  CreditCard,
  FileText,
  Loader2,
  XCircle,
} from 'lucide-react';
import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

const PaymentReceiptPage: React.FC = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams<{ bookingId: string }>();

  // Fetch booking details
  const {
    data: bookingDetails,
    isLoading: isBookingLoading,
    error: bookingError,
  } = useBookingDetails(bookingId || '');

  // Use the payments hook to get all payments
  const { paymentHistory, isLoading: isPaymentsLoading } = usePayments();

  // Find the payment associated with this booking
  const paymentData = React.useMemo(() => {
    if (!bookingId || !paymentHistory.length) return null;

    // First try to find by exact booking ID match
    return paymentHistory.find((payment) => {
      const bidProjectId = payment.id.split('-')[1]; // Extract booking ID if it's part of the payment ID
      return payment.bid_project_id === bookingId || bidProjectId === bookingId;
    });
  }, [bookingId, paymentHistory]);

  // If we couldn't find a payment in the paymentHistory, try to fetch it directly
  const { data: directPaymentData, isLoading: isDirectPaymentLoading } =
    useQuery({
      queryKey: ['directPayment', bookingId],
      queryFn: async () => {
        if (!bookingId) return null;

        const data = await api<any>(`/payments/bid-project/${bookingId}`);
        return data;
      },
      enabled: !!bookingId && !paymentData, // Only run if we have bookingId and didn't find in paymentHistory
    });

  const handleBack = () => {
    navigate(`/filmmaker/dashboard/bookings/${bookingId}`);
  };

  // Format date utility
  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'Not available';
    return format(new Date(dateString), 'MMMM d, yyyy h:mm a');
  };

  // Combine our potentially multiple sources of payment data
  const finalPaymentData = paymentData || directPaymentData;

  // Check if any data is loading
  const isLoading =
    isBookingLoading || isPaymentsLoading || isDirectPaymentLoading;

  // Error state
  const error = bookingError;

  // Loading state
  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <DashboardLayout>
        <div className="flex flex-col justify-center items-center h-64 text-center">
          <XCircle className="h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-xl font-semibold mb-2 text-red-500">
            Error Loading Payment Details
          </h2>
          <p className="text-muted-foreground mb-6">
            {error instanceof Error
              ? error.message
              : 'An unknown error occurred'}
          </p>
          <Button
            onClick={handleBack}
            className="bg-primary hover:bg-primary/90"
          >
            Back to Booking
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  if (!finalPaymentData) {
    return (
      <DashboardLayout>
        <div className="flex flex-col justify-center items-center h-64 text-center">
          <XCircle className="h-12 w-12 text-amber-500 mb-4" />
          <h2 className="text-xl font-semibold mb-2 text-amber-500">
            Payment Not Found
          </h2>
          <p className="text-muted-foreground mb-6">
            No payment record found for this booking.
          </p>
          <Button
            onClick={handleBack}
            className="bg-primary hover:bg-primary/90"
          >
            Back to Booking
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  // Format data for display
  const paymentDate = paymentData?.paymentDate
    ? formatDate(paymentData.paymentDate)
    : directPaymentData?.client_payment_date
    ? formatDate(directPaymentData.client_payment_date)
    : 'Not available';

  const transactionId =
    paymentData?.id || directPaymentData?.payment_id || 'Not available';

  const amount = paymentData?.amount || directPaymentData?.payment_amount || 0;
  const totalAmount = paymentData?.jobAmount || directPaymentData?.amount || 0;
  const adminFee = totalAmount - amount;

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link
                  to="/filmmaker/dashboard"
                  className="text-muted-foreground hover:text-primary"
                >
                  Dashboard
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link
                  to="/filmmaker/dashboard"
                  className="text-muted-foreground hover:text-primary"
                >
                  Bookings
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link
                  to={`/filmmaker/dashboard/bookings/${bookingId}`}
                  className="text-muted-foreground hover:text-primary"
                >
                  {bookingDetails?.title || 'Booking Details'}
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="text-primary font-medium">
                Payment Receipt
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center justify-between mb-6">
          <Button
            variant="outline"
            size="sm"
            className="border-primary/20 text-primary hover:bg-primary/5 hover:text-primary flex items-center gap-1"
            onClick={handleBack}
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Booking
          </Button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="p-8 bg-gradient-to-br from-primary/5 to-primary/10 border-none shadow-md overflow-hidden relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-green-500/5 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl pointer-events-none"></div>

            <div className="flex flex-col items-center text-center relative z-10">
              <div className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 p-4 rounded-full mb-6 shadow-sm">
                <CheckCircle className="h-10 w-10" />
              </div>
              <h1 className="text-3xl font-bold text-green-700 dark:text-green-400 mb-2">
                Payment Received!
              </h1>
              <p className="text-muted-foreground max-w-md text-lg">
                You've received a payment of{' '}
                <span className="text-primary font-bold">
                  £{amount.toLocaleString()}
                </span>{' '}
                for your services.
              </p>
            </div>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card className="border-primary/20 shadow-sm overflow-hidden">
            <div className="bg-primary/5 px-6 py-4 border-b border-primary/10">
              <h2 className="text-lg font-medium">Payment Details</h2>
            </div>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="flex justify-between py-3 border-b border-dashed border-slate-200 dark:border-slate-700">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-primary" />
                    </div>
                    <span className="text-muted-foreground">
                      Transaction ID
                    </span>
                  </div>
                  <div className="font-medium">{transactionId}</div>
                </div>

                <div className="flex justify-between py-3 border-b border-dashed border-slate-200 dark:border-slate-700">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-primary" />
                    </div>
                    <span className="text-muted-foreground">Date</span>
                  </div>
                  <div className="font-medium">{paymentDate}</div>
                </div>

                <div className="flex justify-between py-3 border-b border-dashed border-slate-200 dark:border-slate-700">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
                      <CreditCard className="h-5 w-5 text-primary" />
                    </div>
                    <span className="text-muted-foreground">
                      Payment Method
                    </span>
                  </div>
                  <div className="font-medium">Bank Transfer</div>
                </div>

                <div className="flex justify-between py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
                      <BadgePoundSterling className="h-5 w-5 text-primary" />
                    </div>
                    <span className="text-muted-foreground">
                      Amount Received
                    </span>
                  </div>
                  <div className="font-medium text-green-600 dark:text-green-400">
                    £{amount.toLocaleString()}
                  </div>
                </div>

                {/* Platform fee information */}
                {adminFee > 0 && (
                  <div className="flex justify-between py-3 border-t border-dashed border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
                        <BadgePoundSterling className="h-5 w-5 text-primary" />
                      </div>
                      <span className="text-muted-foreground">
                        Platform Fee
                      </span>
                    </div>
                    <div className="font-medium text-muted-foreground">
                      £{adminFee.toLocaleString()}
                    </div>
                  </div>
                )}

                {/* Total job amount */}
                <div className="flex justify-between py-3 border-t border-dashed border-slate-200 dark:border-slate-700">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
                      <BadgePoundSterling className="h-5 w-5 text-primary" />
                    </div>
                    <span className="text-muted-foreground">
                      Total Job Amount
                    </span>
                  </div>
                  <div className="font-medium">
                    £{totalAmount.toLocaleString()}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <div className="flex justify-center">
          <Button
            variant="outline"
            className="border-primary/20 text-primary hover:bg-primary/5 hover:text-primary"
            onClick={handleBack}
          >
            Return to Booking
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PaymentReceiptPage;
